import React, { useState, useEffect } from 'react';
import { propertiesAPI, Property, PropertyFilter } from '../api/api';
import PropertyCard from '../components/PropertyCard';
import SearchFilters from '../components/SearchFilters';
import { useLanguage } from '../contexts/LanguageContext';

const SearchPage: React.FC = () => {
  const { t } = useLanguage();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<PropertyFilter>({
    page: 1,
    page_size: 20,
    status: 'active',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadProperties();
  }, [filters]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await propertiesAPI.list(filters);
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to load properties', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: PropertyFilter) => {
    setFilters({ ...newFilters, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 md:pb-12">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 mb-2">{t('searchTitle')}</h1>
        <p className="text-gray-500 text-sm sm:text-base">
          {loading ? t('loading') : `${properties.length} ${t('foundCount')}`}
        </p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6 lg:gap-8">
        <div className="lg:col-span-1">
          <div className="sticky top-20">
            <SearchFilters filters={filters} onFilterChange={handleFilterChange} />
          </div>
        </div>

        <div className="lg:col-span-3">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64 sm:h-96">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mb-4"></div>
              <p className="text-gray-500 text-sm font-semibold">{t('loading')}</p>
            </div>
          ) : properties.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {properties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>

              {/* Pagination */}
              <div className="mt-10 flex justify-center items-center gap-3">
                <button
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={filters.page === 1}
                  className="btn btn-secondary px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold shadow-sm"
                >
                  ← {t('previous')}
                </button>
                <div className="px-5 py-2.5 bg-white border-2 border-primary-600 text-primary-700 rounded-xl font-bold text-sm shadow-sm">
                  {t('page')} {filters.page}
                </div>
                <button
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={properties.length < filters.page_size!}
                  className="btn btn-secondary px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold shadow-sm"
                >
                  {t('next')} →
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-20 sm:py-32 card p-8">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                {t('noResults')}
              </h3>
              <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">{t('noResultsDescription')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
